import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import type { HierarchicalCallReasonData } from "@/hooks/useHierarchicalCallReasons";
import { callsPath } from "@/lib/callsUrlState";
import { formatCount, formatPercent, formatPointsDelta } from "@/lib/format";
import { indicatorLabel } from "@/lib/indicatorDefinitions";

/**
 * Bloc « À examiner » — plan de modernisation UX, §4.
 *
 * Première version volontairement simple : des règles compréhensibles et
 * affichées, sans statistique avancée. Un motif est signalé lorsqu'il réunit
 * un volume suffisant et s'écarte nettement de l'ensemble du périmètre. La
 * comparaison porte sur l'ensemble de la période, pas sur une évolution dans
 * le temps : la période précédente n'est pas encore calculée côté serveur.
 */

const MIN_CALLS = 20;
const MIN_SHARE = 0.02;
const MIN_GAP_POINTS = 10;
const MAX_TOPICS = 4;

interface Topic {
  reason: string;
  calls: number;
  metric: "resolution_status" | "reiteration";
  value: number;
  gap: number;
  /** Écart rapporté au volume, pour ordonner les sujets. */
  weight: number;
}

interface ReviewTopicsProps {
  data: HierarchicalCallReasonData[] | undefined;
  isLoading: boolean;
  isSalesMode: boolean;
  globalResolutionRate: number;
  globalReiterationRate: number;
  totalCalls: number;
  dateRange: { from?: string; to?: string };
}

export function ReviewTopics({
  data, isLoading, isSalesMode, globalResolutionRate, globalReiterationRate, totalCalls, dateRange,
}: ReviewTopicsProps) {
  const minCalls = Math.max(MIN_CALLS, Math.ceil(totalCalls * MIN_SHARE));

  const byParent = new Map<string, { calls: number; resolved: number; reiterated: number }>();
  (data ?? []).forEach((row) => {
    const entry = byParent.get(row.parent_reason) ?? { calls: 0, resolved: 0, reiterated: 0 };
    entry.calls += row.call_count;
    entry.resolved += (row.resolution_rate || 0) * row.call_count;
    entry.reiterated += (row.reiteration_rate || 0) * row.call_count;
    byParent.set(row.parent_reason, entry);
  });

  const topics: Topic[] = [];
  byParent.forEach((entry, reason) => {
    if (!reason || entry.calls < minCalls) return;
    const resolution = entry.resolved / entry.calls;
    const reiteration = entry.reiterated / entry.calls;
    if (!isSalesMode && globalResolutionRate - resolution >= MIN_GAP_POINTS) {
      const gap = resolution - globalResolutionRate;
      topics.push({ reason, calls: entry.calls, metric: "resolution_status", value: resolution, gap, weight: -gap * entry.calls });
    }
    if (reiteration - globalReiterationRate >= MIN_GAP_POINTS) {
      const gap = reiteration - globalReiterationRate;
      topics.push({ reason, calls: entry.calls, metric: "reiteration", value: reiteration, gap, weight: gap * entry.calls });
    }
  });
  topics.sort((a, b) => b.weight - a.weight);
  const shown = topics.slice(0, MAX_TOPICS);

  const linkFor = (topic: Topic) =>
    callsPath({
      dateRange,
      selectedReasonParents: [topic.reason],
      ...(topic.metric === "resolution_status"
        ? { resolutionFilter: ["Non résolu", "Résolu partiellement"] }
        : { reiterationFilter: ["Deuxième itération", "Troisième itération et plus"] }),
    });

  return (
    <section aria-labelledby="a-examiner" className="rounded-xl border border-border bg-card p-4">
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
        <h2 id="a-examiner" className="text-base font-semibold">À examiner</h2>
        <p className="text-sm text-muted-foreground">
          Motifs d'au moins {formatCount(minCalls)} appels dont le taux s'écarte d'au moins {MIN_GAP_POINTS} points de l'ensemble de la période.
        </p>
      </div>

      {isLoading ? (
        <div className="space-y-2">
          {[0, 1, 2].map((i) => <div key={i} className="h-12 rounded-lg bg-muted animate-pulse" />)}
        </div>
      ) : shown.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {data && data.length > 0
            ? "Aucun motif ne dépasse ces seuils sur la période."
            : "Données insuffisantes : aucun motif n'est encore classé sur la période."}
        </p>
      ) : (
        <ul className="divide-y divide-border">
          {shown.map((topic) => (
            <li key={`${topic.reason}-${topic.metric}`} className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 py-2.5 first:pt-0 last:pb-0">
              <div className="min-w-0">
                <p className="font-medium text-foreground">{topic.reason}</p>
                <p className="text-sm text-muted-foreground tabular-nums">
                  {indicatorLabel(topic.metric)} {formatPercent(topic.value)} · {formatPointsDelta(topic.gap)} par rapport à l'ensemble · {formatCount(topic.calls)} appels
                </p>
              </div>
              <Link
                to={linkFor(topic)}
                className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline underline-offset-4"
              >
                {topic.metric === "resolution_status" ? "Voir les appels non résolus" : "Voir les réitérations"}
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
